import {
  useArticleCommentsQuery,
  CommentType,
} from '@buildwise/pages/article/hooks/useArticleDetailsQuery';
import { orderBy } from 'lodash';
import { useMemo, useState } from 'react';
import {
  formatDate,
  getExtraFileNameAttachedByService,
  replaceMentions,
  splitFilesByType,
} from 'utility/helpers';
import { Title } from './PostInfo';
import { ArticleType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import Dot from 'components/common/dot/Dot';
import RenderDocuments from '@buildwise/pages/article/components/RenderDocuments';
import Thumbnails from 'components/common/dropzone/Thumbnails';
import LightboxModal from 'components/common/modal/LightboxModal';

type RenderFacilitatorNotesProps = {
  article: ArticleType;
};

function RenderFacilitatorNotes({ article }: RenderFacilitatorNotesProps) {
  const { comments } = useArticleCommentsQuery({
    articleId: article?.id || '',
  });
  const lastFacilitatorNote: CommentType | undefined = useMemo(() => {
    return orderBy(
      comments.filter(
        (lastFacilitatorNote) =>
          lastFacilitatorNote.objectType === 'Notes' &&
          lastFacilitatorNote.objectSubType === 'FacilitatorNotes',
      ),
      (value) => new Date(value.commentedAt),
      'desc',
    )?.[0];
  }, [comments]);
  const [lightboxOpen, setLightboxOpen] = useState<{
    open: boolean;
    fileName: string;
    action?: string;
  }>({
    open: false,
    fileName: '',
    action: '',
  });
  const { documents, mediaFiles } = useMemo(
    () =>
      splitFilesByType({
        files: lastFacilitatorNote?.attachments || [],
        extraFileNameAttachedByService: getExtraFileNameAttachedByService(lastFacilitatorNote),
        doNotIncludeDocumentsThumbnails: true,
      }),
    [lastFacilitatorNote],
  );
  const RenderedComment = useMemo(() => {
    if (!lastFacilitatorNote) return null;
    const _commentText = replaceMentions(
      lastFacilitatorNote.commentText,
      lastFacilitatorNote.mentions,
    );
    return <CommentText dangerouslySetInnerHTML={{ __html: _commentText }} />;
  }, [lastFacilitatorNote]);

  if (!lastFacilitatorNote) {
    return null;
  }

  return (
    <Box mb={1}>
      <Box display="flex" alignItems="center" mt={1.5}>
        <Title sx={{ mt: 0 }}>Facilitator Notes</Title>
        <Dot />
        <Typography variant="caption" sx={{ fontSize: 12 }}>
          {formatDate(lastFacilitatorNote?.commentedAt)}
        </Typography>
      </Box>
      {RenderedComment}
      {mediaFiles.length > 0 && (
        <Thumbnails<CommentType>
          files={mediaFiles}
          mode="view"
          setLightboxOpen={setLightboxOpen}
          showCoverPhotoIndicator={false}
          item={lastFacilitatorNote}
        />
      )}
      <RenderDocuments documents={documents} showTitle={false} />

      {lightboxOpen.open && (
        <LightboxModal<CommentType>
          item={lastFacilitatorNote}
          onClose={() => setLightboxOpen({ open: false, fileName: '' })}
          open={lightboxOpen.open}
          mediaFiles={mediaFiles}
          defaultOpenFileName={lightboxOpen.fileName}
          hideCoverPhotoOption
          mode="view"
        />
      )}
    </Box>
  );
}

const CommentText = styled(Typography, { label: 'CommentText' })(({ theme }) => ({
  display: 'inline',
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '17px',
  marginTop: 6,
  whiteSpace: 'pre-line',
  '& .highlight': {
    backgroundColor: `${theme.palette.custom.paleGold} !important`,
  },
  '& .mention': {
    backgroundColor: theme.palette.custom.aliceBlue,
    color: theme.palette.custom.link,
    borderRadius: 4,
    padding: 2,
  },
}));

export default RenderFacilitatorNotes;
